import React, { useMemo } from 'react';
import {
  Filters,
  FiltersLayout,
  GraphQLList,
  ListPageLayout,
  SearchInput,
  type LoadingParams,
  useListContext,
} from 'framework/list';
import { EmployeeFilter } from 'domain/shared/employee';
import {
  STAFFING_PRIORIRY_ZONES_ALIASES,
  HealthZone,
  StaffingDataType,
  StaffingRiskByPriority,
} from 'domain/shared/staffing';
import TeamDashboardZone from './StaffingDashboardZone';
import { GET_STAFFING_DASHBOARD_DATA } from '../graphql';
import { staffingDataGetters } from '../utils/staffingDataGetters';
import { DashboardProvider } from 'framework/ui/dashboard';
import { PROJECT_MEMBERSHIP_ROLES_KEY } from 'domain/shared/core/constants';

type DashboardSearchFilter = {
  filter: {
    isArchived?: { ne_or_null: boolean };
    projectState?: { ne: string };
    name?: { ct: string };
    staffingCoordinator?: { eq: string };
    'specification:hasAccountManager'?: { eq: string };
    'specification:hasTeamMember'?: {
      eq: {
        alias: string;
        role: typeof PROJECT_MEMBERSHIP_ROLES_KEY.PROJECT_MANAGER;
      };
    };
  };
};

const StaffingDashboardContent: React.FC = () => {
  const { data } = useListContext();

  const projectsByStatus = useMemo((): StaffingRiskByPriority => {
    const projects = data?.items || [];
    const grouped: StaffingRiskByPriority = {
      SAFE: [],
      MONITORING: [],
      CRITICAL: [],
    };

    projects.forEach((project: StaffingDataType) => {
      const backendZone = project.staffingRisk?.priority || STAFFING_PRIORIRY_ZONES_ALIASES.SAFE;

      if (STAFFING_PRIORIRY_ZONES_ALIASES[backendZone]) {
        grouped[backendZone as keyof StaffingRiskByPriority].push(project);
      } else {
        grouped.SAFE.push(project);
      }
    });

    return grouped;
  }, [data?.items]);

  return (
    <ListPageLayout
      sxPaper={{ p: '10px!important' }}
      filter={
        <Filters>
          <FiltersLayout>
            <EmployeeFilter source="projectManager" label="Project manager" />
            <EmployeeFilter source="ownerAlias" label="Account manager" />
            <EmployeeFilter source="staffingCoordinator" label="Staffing coordinator" />
          </FiltersLayout>
        </Filters>
      }
      search={<SearchInput label="project name" />}
    >
      <DashboardProvider dataGetters={staffingDataGetters}>
        {Object.entries(STAFFING_PRIORIRY_ZONES_ALIASES)
          .reverse()
          .map(([key, alias]) => {
            const zone = alias as HealthZone;
            const zoneKey = alias as HealthZone;
            return <TeamDashboardZone key={key} zone={zone} projects={projectsByStatus[zoneKey]} />;
          })}
      </DashboardProvider>
    </ListPageLayout>
  );
};

const StaffingDashboard: React.FC = () => {
  const transformVariables = (params: LoadingParams) => {
    let searchString = '';
    const staffingCoordinator = params.filter?.staffingCoordinator?.eq;
    const projectManager = params.filter?.projectManager?.eq;
    const ownerAlias = params.filter?.ownerAlias?.eq;

    if (params.filter?.['specification:search']?.eq) {
      searchString = params.filter['specification:search'].eq;
    }

    const createSearchString = (searchTerm: string): string => {
      const filterObject: DashboardSearchFilter = {
        filter: {},
      };

      filterObject.filter.isArchived = { ne_or_null: true };
      filterObject.filter.projectState = { ne: 'PRESTART' };

      if (searchTerm && searchTerm.trim()) {
        filterObject.filter.name = { ct: searchTerm.trim() };
      }

      if (projectManager) {
        filterObject.filter['specification:hasTeamMember'] = {
          eq: {
            alias: projectManager,
            role: PROJECT_MEMBERSHIP_ROLES_KEY.PROJECT_MANAGER,
          },
        };
      }

      if (staffingCoordinator) {
        filterObject.filter.staffingCoordinator = { eq: staffingCoordinator };
      }

      if (ownerAlias) {
        filterObject.filter['specification:hasAccountManager'] = { eq: ownerAlias };
      }

      return JSON.stringify(filterObject);
    };

    return {
      searchString: createSearchString(searchString),
    };
  };

  return (
    <GraphQLList query={GET_STAFFING_DASHBOARD_DATA} dataPath="baseProjects" transformVariables={transformVariables}>
      <StaffingDashboardContent />
    </GraphQLList>
  );
};

export default StaffingDashboard;
